import React, { useState, useEffect } from "react";
import axios from "axios";
import API from "@/utils/api";
import RiderLayout from "./RiderLayout";
import { Clock, TrendingUp, Search, Calendar, Landmark, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function RiderHistory() {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);

    const fetchData = async () => {
        try {
            const token = localStorage.getItem("rider_token");
            if (!token) return;

            const [historyRes, statsRes] = await Promise.all([
                axios.get(`${API}/rider/history?limit=100`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/rider/stats/earnings`, { headers: { Authorization: `Bearer ${token}` } })
            ]);

            setHistory(historyRes.data);
            setStats(statsRes.data);
        } catch (error) {
            console.error("Error fetching history", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    if (loading) {
        return (
            <RiderLayout>
                <div className="flex flex-col items-center justify-center min-h-[60vh] animate-pulse">
                    <Clock size={48} className="text-slate-800 mb-4" />
                    <div className="h-4 w-32 bg-slate-800 rounded" />
                </div>
            </RiderLayout>
        );
    }

    return (
        <RiderLayout>
            <div className="p-4 space-y-6 pb-24">
                {/* Header Stats */}
                <div className="flex gap-4">
                    <div className="flex-1 bg-white/5 border border-white/10 rounded-2xl p-4">
                        <span className="text-[10px] uppercase font-black tracking-widest text-slate-500 block mb-1">Total Entregas</span>
                        <span className="text-2xl font-black text-white">{history.filter(j => j.status === 'delivered').length}</span>
                    </div>
                    <div className="flex-1 bg-white/5 border border-white/10 rounded-2xl p-4">
                        <span className="text-[10px] uppercase font-black tracking-widest text-slate-500 block mb-1">Acumulado</span>
                        <span className="text-2xl font-black text-green-400">{stats?.total_lifetime?.toFixed(2)}{stats?.symbol || "€"}</span>
                    </div>
                </div>

                {/* Search Bar (Placeholder for now) */}
                <div className="relative group">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 transition-colors group-focus-within:text-primary" size={18} />
                    <input
                        type="text"
                        placeholder="Buscar por ID o Restaurante..."
                        className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-sm font-bold text-white focus:outline-none focus:border-primary/50 transition-all"
                    />
                </div>

                {/* History List */}
                <div className="space-y-4">
                    <h2 className="text-xs font-black uppercase tracking-widest text-slate-400 flex items-center gap-2 px-1">
                        <Calendar size={14} className="text-primary" />
                        Historial Completo
                    </h2>

                    <div className="space-y-2">
                        {history.length === 0 ? (
                            <div className="text-center py-12 bg-white/5 border border-dashed border-white/10 rounded-3xl">
                                <p className="text-sm text-slate-500 font-medium italic">Aún no has realizado ninguna entrega.</p>
                            </div>
                        ) : (
                            history.map((job) => (
                                <div key={job.job_id} className="bg-white/5 border border-white/10 rounded-3xl p-5 hover:bg-white/10 transition-all border-l-4 border-l-transparent data-[status=delivered]:border-l-green-500 data-[status=cancelled]:border-l-red-500" data-status={job.status}>
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="font-black text-sm text-white uppercase">{job.restaurant_name}</h3>
                                                {job.rider_paid && (
                                                    <span className="text-[8px] bg-green-500/20 text-green-500 px-2 py-0.5 rounded-full font-black uppercase ring-1 ring-green-500/30">Liquidado</span>
                                                )}
                                            </div>
                                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter">#{job.job_id.slice(-8).toUpperCase()}</p>
                                        </div>
                                        <div className="text-right">
                                            <span className={`text-lg font-black ${job.status === 'delivered' ? 'text-white' : 'text-slate-600 line-through'}`}>
                                                +{(job.rider_earnings + (job.tip_amount || 0) + (job.bonus_amount || 0)).toFixed(2)}{stats?.symbol || "€"}
                                            </span>
                                            <div className="flex items-center gap-1.5 justify-end mt-1">
                                                {job.status === 'delivered' ? <CheckCircle2 size={12} className="text-green-500" /> : <XCircle size={12} className="text-red-500" />}
                                                <span className={`text-[10px] font-black uppercase ${job.status === 'delivered' ? 'text-green-500' : 'text-red-500'}`}>
                                                    {job.status === 'delivered' ? 'Listo' : 'Cancelado'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 pt-3 border-t border-white/5">
                                        <div>
                                            <span className="text-[9px] text-slate-500 font-black uppercase block mb-0.5">Fecha</span>
                                            <span className="text-[11px] font-bold text-slate-300">{new Date(job.created_at).toLocaleDateString()}</span>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-[9px] text-slate-500 font-black uppercase block mb-0.5">Distancia</span>
                                            <span className="text-[11px] font-bold text-slate-300">{job.distance_km.toFixed(1)} km</span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </RiderLayout>
    );
}
